define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dijit/layout/TabContainer",
	"dijit/layout/TabController",
	"csi",
	"csi/_base/underscore",
	"csi/action",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/def/Action",
	"csi/html",
	"csi/utility/routerUtility",
	"csi/tabs/Content",
	"csi/tabs/ScrollingTabController",
	"csi/view/Widget",
	"dojo/text!csi/templates/tabs.html"
], function(declare, lang, aspect, TabContainer, TabController,
	csi, _, action, comm, PostRequest, Action, html, {checkNavigation}, Content, ScrollingTabController, Widget, template) {

	/**
	 * We introducing this to be able to freeze dom node before it resize (to prevent flicker in non wizard popups with tabs)
	 * Resize will happen in this.inherited call
	 */
	var _TabContainer = declare(TabContainer, {
		_hideChild: function(/*dijit/_WidgetBase*/ page) {
			if (this.getParent().bPopup) {
				var domNode = html.getParentByClassName(page.domNode, 'dijitTabPaneWrapper');
				html.freezeDomNode(domNode, true);
			}
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Replace dijit ScrollingTabController with our widget
		 */
		postMixInProperties: function() {
			if (!this.controllerWidget) {
				this.controllerWidget = (this.tabPosition == "top" || this.tabPosition == "bottom") && !this.nested ?
					ScrollingTabController : TabController;
			}
			this.inherited(arguments);
		}
	});

	return declare(Widget, {
		allowChildToScroll: true,
		templateString: template,

		// connection points
		headerNode: null,
		tabsNode: null,

		// Widget: header view (if any)
		//headerView: null,

		/** @property {DOMNode} containerNode - the container for the tab view */
		containerNode: null,

		// Boolean: indicates if tab view is nested in which case styling is different
		nestedTabs: false,

		/** @property {Widget} tabContainerClass - the corresponding widget container for the view(tab or wizard) */
		tabContainerClass: _TabContainer,

		/** @property {DOMNode} tabContainerNode - the container for the wizard view */
		tabContainerNode: null,

		/** @property {string} tabLocationType - used to override current location type when determining tabs to be shown (see def#_getTypeViewDef) */
		tabLocationType: null,

		// Object: handle for selecting tab
		//_selectHandle: null,

		destroy: function() {
			// summary: reset pointer just to be sure no more references to view
			this.headerView = null;
			this.inherited(arguments);
		},

		resize: function() {
			// summary: forward resize event to tab container
			if (this.tabsNode && this.isValidView()) {
				this.tabsNode.resize();
			}
		},

		addTab: function(/*Tab*/tab) {
			// summary: add tab to view
			var w = new Content({defTab: tab});

			this.tabsNode.addChild(w);

			if (this.tabsNode._started) {
				w.refreshTab(this.getContext());
			}

			return w;
		},

		getTab: function(/*String*/idTab, /*Boolean?*/bTestSelect) {
			// summary: find tab by id
			if (this.tabsNode) {
				return csi.search(this.tabsNode.getChildren(), function(w) {
					return (w.defTab.m_idView == idTab) && (!bTestSelect || w.canBeSelected());
				});
			}

			return null;
		},

		/**
		 * Return type that will be used to check if tabs can be accessed
		 * NOTE: this is to workaround def.js use of routerStore.locationType
		 * @return {string | null}
		 */
		getTabLocationType() {
			let view = this;

			while (view) {
				if (view.tabLocationType) {
					return view.tabLocationType;
				}

				view = view.viewParent;
			}

			return null;
		},

		selectTab: function(/*Content*/target) {
			// summary: select target tab if valid - otherwise use first valid tab
			var tc = this.tabsNode;
			if (!tc) return null;

			if (!target || !target.canBeSelected()) {
				target = csi.search(tc.getChildren(), function(w) {
					return w.canBeSelected();
				});
			}

			// select and return tab
			if (target) {
				html.setVisible(tc, true);
				tc.selectChild(target);
				return target;
			}

			// no target tab
			html.setVisible(tc, false);
			return null;
		},

		_getRootRequest: function(/*Node*/node) {
			// summary: return request used to query for root object of view with dependencies
			if (!node) return null;

			var defView = this.defView;
			var urls = [node.$URL];
			var paths = [];

			// determine if tabs have different node than current - if so, need to query for them
			_.each(defView.tabs, function(tab) {
				tab.getQueryParts(node, urls, paths);
			});

			// add explicit dependency paths
			if (defView.m_depPaths) {
				paths = paths.concat(defView.m_depPaths);
			}

			urls = _.uniq(urls);
			paths = _.uniq(paths);

			return new PostRequest(comm.MOD_SEARCH, comm.OP_URL, {"Qry.URL": urls}).addDependencyPaths(paths);
		},

		_getTargetTab: function() {
			// summary: return tab to build based on browser location or last tab visited
			var children = this.tabsNode.getChildren();
			return children[0];
		},

		_redraw: function() {
			// summary: redraw tabs and current view
			if (this.tabsNode) {
				this.tabsNode.destroyRecursive();
				this.tabsNode = null;
			}

			this.inherited(arguments);
		},

		_refreshWidget: function() {
			// summary: destroy header view and redraw
			if (this.headerView) {
				this.headerView.destroyView();
				this.headerView = null;
			}

			this.inherited(arguments);
		},

		_buildView: function() {
			// summary: populate tabs first time - otherwise just refresh
			var self = this;
			var ctx = this.getContext();
			var node = ctx.getNode();
			var tc = this.tabsNode;
			var target;
			var dfd;

			if (this.headerView) {
				// if header view already created, then just refresh
				dfd = this.headerView.refreshWidget();
			} else if (this.defView.idHeaderView) {
				// create and render header view before updating tab view
				html.setVisible(this.headerNode, true);

				dfd = this._insertAndCreateView(this.defView.idHeaderView, node, this.headerNode);

				dfd.then(function(view) {
					self.headerView = view;
				});
			}

			if (tc) {
				// if refreshing, target the currently selected tab
				target = tc.selectedChildWidget;
			} else if (this.defView.tabs) {
				// create tab container with all tabs
				tc = this.tabsNode = new this.tabContainerClass({
					doLayout: false,
					nested: this.nestedTabs,
					tabPosition: "top"
				}, html.create("div", null, this.tabContainerNode || this.containerNode));

				_.each(this.defView.tabs, this.addTab, this);

				tc.startup();
				this._selectHandle = null;
			}

			if (tc) {
				// set tab name and visibility
				_.each(tc.getChildren(), function(w) {
					w.refreshTab(ctx);
				});

				// now that we know which tabs are visible, find the one to select
				target = target || this._getTargetTab();

				// finally, render target tab - if no target tab, then go directly to _postView
				this.when(dfd, () => this._buildTab(target));
			} else {
				this._postView();
			}
		},

		_buildTab: function(/*Content?*/target) {
			// summary: select targeted tab before finished with loading view
			const {tabsNode} = this;
			var tab = this.selectTab(target);
			var dfd;

			if (tab) {
				html.setVisible(tabsNode, true);
				dfd = tab.createView(this);

				if (!dfd) {
					html.setVisible(tabsNode, false);
				}
			}

			// listen for tab selections, but only after initial tab is selected
			if (!this._selectHandle) {
				this._selectHandle = this.own(aspect.after(tabsNode.tablist, "onSelectChild", (target) => this._onSelectTab(target), true));

				this.own(aspect.around(tabsNode, "selectChild", function(originalFunction) {
					// add ablity to prevent tab switching if user is in middle of an action
					return async function(page, animate) {
						const allowed = await checkNavigation(false, true);
						if (allowed) {
							originalFunction.apply(tabsNode, arguments);
						}
					};
				}));
			}

			this.when(dfd, () => this._postView());
		},

		_onSelectTab: function(/*Content*/target) {
			// summary: invoke action to switch tabs
			action.invoke(this, this.domNode, lang.mixin(new Action(), {
				m_id: "TabsSelect",
				m_postAction: function(ac) {
					ac.setProgress();
					return target.createView(ac.widget);
				}
			}));
		},

		_postView: function() {
			// summary: resize after view is loaded in case size changed due to child views
			if (this.isValidView()) {
				this.resize();
			}

			return this.inherited(arguments);
		}
	});
});